<script setup lang="ts">
import VarsityInlineConfirm from "@/components/VarsityInlineConfirm.vue"
import VarsityInlineConfirmButton from "@/components/VarsityInlineConfirmButton.vue"
import { createInlineConfirm } from "@/composables/createInlineConfirm"
import type {
    ChampionshipRegistrationSelectTeamTask
} from "@/model/championship/championship-registration-select-team-task"
import type { TasksProps } from "@/pages/Tasks/Tasks/tasks-props"
import { defineTranslations } from "@/utils/defineTranslations"
import { useHttpClient, useHttpGet } from "@/vf"
import AnimateIf from "@/vf/components/AnimateIf.vue"
import { computed, provide, ref, toRefs } from "vue"
import { type ChampionshipRegistrationSelectTeamData, SelectTeamDataInjection } from "./championship-registration"
import ChampionshipRegistrationEditDebitorProfile from "./internal/ChampionshipRegistrationEditDebitorProfile.vue"
import ChampionshipRegistrationNewTeamWizard from "./internal/ChampionshipRegistrationNewTeamWizard.vue"
import ChampionshipRegistrationTeamList from "./internal/ChampionshipRegistrationTeamList.vue"
import { useRegistrationPolicy } from "./useRegistrationPolicy"

const props = defineProps<TasksProps<ChampionshipRegistrationSelectTeamTask>>()

const emit = defineEmits<{
    (e: "updated"): void
}>()

const { task } = toRefs(props)
const http = useHttpClient()
const whatToDo = ref<"new_team" | "change_debitor_profile" | null>(null)

/*─────────────────────────────────────┐
│  list                                │
└─────────────────────────────────────*/
const { data, refresh } = await useHttpGet<ChampionshipRegistrationSelectTeamData>(
    `/championship/registration/${task.value.organizationId}/select-team/${task.value.id}`,
)

provide(SelectTeamDataInjection, data)
const policy = useRegistrationPolicy(
    computed(() => data.value?.permissions),
    computed(() => props.viewMode),
)

/*─────────────────────────────────────┐
│  refresh list                        │
└─────────────────────────────────────*/
async function onUpdated() {
    refresh()
    whatToDo.value = null
}

/*─────────────────────────────────────┐
│  finalize                            │
└─────────────────────────────────────*/
async function finalizeRegistration() {
    await http.post(`/championship/registration/${data.value!.registrationId}/finalize?viewMode=${props.viewMode}`)
    emit("updated")
}

/*─────────────────────────────────────┐
│  Allow after registration period     │
└─────────────────────────────────────*/
const openConfirm = createInlineConfirm({
    async action() {
        await http.post(
            `/championship/registration/${data.value?.registrationId}/toggle-allow-after-registration-period?viewMode=${props.viewMode}`,
            {
                allow: true,
            },
        )
    },
    afterAction() {
        refresh()
    },
})
const closeConfirm = createInlineConfirm({
    async action() {
        await http.post(
            `/championship/registration/${data.value?.registrationId}/toggle-allow-after-registration-period?viewMode=${props.viewMode}`,
            {
                allow: false,
            },
        )
    },
    afterAction() {
        refresh()
    },
})
const resetConfirm = createInlineConfirm({
    async action() {
        await http.post(
            `/championship/registration/${data.value?.registrationId}/reset-after-registration-period?viewMode=${props.viewMode}`,
        )
    },
    afterAction() {
        emit("updated")
        refresh()
    },
})

const trans = defineTranslations({
    "registration_closed": {
        de: "Die Anmeldung zur Meisterschaft ist bereits geschlossen.",
        en: "The registration for the championship is already closed.",
    },
    "open_task.button": {
        de: "Task öffnen",
        en: "Open task",
    },
    "open_task.confirm": {
        de: "Soll der Task wirklich geöffnet werden damit die Organisation trotz Meldeschluss Änderungen vornehmen kann?",
        en: "Do you really want to open the task so that the organization can make changes despite the registration deadline?",
    },
    "open_task.success": {
        de: "Der Task wurde geöffnet. Die Organisation kann nun Änderungen vornehmen.",
        en: "The task has been opened. The organization can now make changes.",
    },
    "close_task.info": {
        de: "Dieser Task wurde manuell für die Organisation geöffnet, damit sie nach dem Meldeschluss noch Änderungen vornehmen kann.",
        en: "This task has been manually opened for the organization so that they can still make changes after the registration deadline.",
    },
    "close_task.button": {
        de: "Task schließen",
        en: "Close task",
    },
    "close_task.confirm": {
        de: "Soll der Task wirklich geschlossen werden, sodass die Organisation keine Änderungen mehr vornehmen kann?",
        en: "Do you really want to close the task so that the organization can no longer make changes?",
    },
    "close_task.success": {
        de: "Der Task wurde geschlossen. Die Organisation kann nun keine Änderungen mehr vornehmen.",
        en: "The task has been closed. The organization can no longer make changes.",
    },
    "reset_registration.button": {
        de: "Meldung zurücksetzen",
        en: "Reset registration",
    },
    "reset_registration.confirm": {
        de: "Soll die Anmeldung wirklich zurückgesetzt werden? Die Anmeldung muss dann neu finalisiert werden",
        en: "Do you really want to reset the registration? The registration must then be finalized again",
    },
    "reset_registration.success": {
        de: "Die Anmeldung wurde zurückgesetzt.",
        en: "The registration has been reset.",
    },
})
</script>
<template>
    <div v-if="data?.isRegistrationClosed && !data?.allowSelectTeamAfterRegistrationPeriod" class="p-5">
        <div class="alert alert-primary">
            {{ trans("registration_closed") }}
        </div>

        <div class="mt-5" v-if="policy.ResetAfterRegistrationPeriod">
            <VarsityInlineConfirmButton :controller="resetConfirm">
                {{ trans("reset_registration.button") }}
            </VarsityInlineConfirmButton>
            <VarsityInlineConfirm :controller="resetConfirm">
                <template #confirmation>
                    {{ trans("reset_registration.confirm") }}
                </template>
                <template #success>
                    {{ trans("reset_registration.success") }}
                </template>
            </VarsityInlineConfirm>
        </div>

        <div class="mt-5" v-if="policy.ToggleAllowAfterRegistrationPeriod">
            <VarsityInlineConfirmButton :controller="openConfirm">
                {{ trans("open_task.button") }}
            </VarsityInlineConfirmButton>
            <VarsityInlineConfirm :controller="openConfirm">
                <template #confirmation>
                    {{ trans("open_task.confirm") }}
                </template>
                <template #success>
                    {{ trans("open_task.success") }}
                </template>
            </VarsityInlineConfirm>
        </div>
    </div>
    <div v-else-if="task.state === 'open' && data">
        <div class="p-5 form-light" v-if="!data.debitorProfile">
            <b>
                {{ $t("@tasks:tasks.championship_registration.wizard.provide_debitor_profile_first") }}
            </b>
            <ChampionshipRegistrationEditDebitorProfile v-bind="{ data }" @updated="onUpdated" />
        </div>
        <div class="p-5 form-light" v-else>
            <b>{{ $t("@tasks:tasks.championship_registration.description.title") }}</b>
            <div class="mb-4">{{ $t("@tasks:tasks.championship_registration.description.text") }}</div>

            <div class="my-5 pb-3">
                <b class="d-block mt-4">{{ $t("@tasks:tasks.championship_registration.wizard.what_to_do") }}</b>

                <button
                    type="button"
                    class="wizard-button"
                    @click="whatToDo = 'new_team'"
                    :class="{ dark: whatToDo === 'new_team' }"
                    :disabled="!policy.RegisterBlankTeam"
                >
                    {{ $t("@tasks:tasks.championship_registration.wizard.register_team") }}
                </button>

                <AnimateIf :if="whatToDo === 'new_team'">
                    <ChampionshipRegistrationNewTeamWizard v-bind="{ data }" @saved="onUpdated" />
                </AnimateIf>

                <button
                    type="button"
                    class="wizard-button"
                    @click="whatToDo = 'change_debitor_profile'"
                    :class="{ dark: whatToDo === 'change_debitor_profile' }"
                    :disabled="!policy.ChangeDebitorProfile"
                >
                    {{ $t("@tasks:tasks.championship_registration.wizard.change_debitor_profile") }}
                </button>

                <AnimateIf :if="whatToDo === 'change_debitor_profile'">
                    <div class="p-5 form-light">
                        <b>
                            {{ $t("@tasks:tasks.championship_registration.wizard.please_provide_new_debitor_profile") }}
                        </b>
                        <ChampionshipRegistrationEditDebitorProfile v-bind="{ data }" @updated="onUpdated" />
                    </div>
                </AnimateIf>

                <button
                    class="wizard-button"
                    type="button"
                    v-if="data.teams.length > 0"
                    @click="finalizeRegistration()"
                    :disabled="!policy.FinalizeRegistration"
                >
                    {{ $t("@tasks:tasks.championship_registration.wizard.finalize_registration") }}
                </button>
            </div>

            <b class="my-2 d-block">
                {{ $t("@tasks:tasks.championship_registration.debitor_profile.text", { ...data.debitorProfile }) }}
            </b>

            <ChampionshipRegistrationTeamList
                :registration-id="data.registrationId"
                :teams="data.teams"
                :show-price="false"
                :show-edit="true"
                :show-delete="true"
                :view-mode="props.viewMode"
                :permissions="data.permissions"
                @updated="refresh()"
            />

            <div class="mt-5"></div>

            <div class="alert alert-warning" v-if="data.allowSelectTeamAfterRegistrationPeriod">
                {{ trans("close_task.info") }}
            </div>

            <div class="d-flex justify-content-between">
                <div v-if="policy.ToggleAllowAfterRegistrationPeriod && data.allowSelectTeamAfterRegistrationPeriod">
                    <VarsityInlineConfirmButton :controller="closeConfirm">
                        {{ trans("close_task.button") }}
                    </VarsityInlineConfirmButton>
                </div>

                <button
                    class="btn btn-primary"
                    type="button"
                    :disabled="data.teams.length === 0"
                    @click="finalizeRegistration()"
                    v-if="policy.FinalizeRegistration"
                >
                    {{ $t("@tasks:tasks.championship_registration.wizard.finalize_registration") }}
                </button>
            </div>

            <VarsityInlineConfirm :controller="closeConfirm">
                <template #confirmation>
                    {{ trans("close_task.confirm") }}
                </template>
                <template #success>
                    {{ trans("close_task.success") }}
                </template>
            </VarsityInlineConfirm>
        </div>
    </div>
</template>
